import React, { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Etapa as EtapaEtapa } from "shared/features/api/etapa/etapaSlice";
import { Etapa as EtapaEdicao } from "shared/features/api/edicao/edicaoSlice";

import { useGetEtapasQuery } from "shared/features/api/etapa/etapaSlice";
import { FormModal } from "components/FormModal";
import { SortableTableRow } from "components/SortableTableRow";

interface EtapasEdicaoTableProps {
  etapas: EtapaEdicao[];
  onEtapasChange: (etapas: EtapaEdicao[]) => void;
}

export const EtapasEdicaoTable: React.FC<EtapasEdicaoTableProps> = ({ etapas, onEtapasChange }) => {
  const [open, setOpen] = useState<boolean>(false);
  const {data: etapasGerais} = useGetEtapasQuery();
  const [etapasSelect, setEtapasSelect] = useState<EtapaEtapa[]>([]);
  const [etapaSelecionada, setEtapaSelecionada] = useState<number>(0);

  useEffect(() => {
    if(etapasGerais) {
      const etapasSelect = etapasGerais?.filter(
        (etapa: EtapaEtapa) => !etapas.some(e => e.idEtapa === etapa.idEtapa)
      );
      setEtapasSelect(etapasSelect)
    }
  },[etapas, etapasGerais])


  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = etapas.findIndex((item) => item.idEtapa === active.id);
      const newIndex = etapas.findIndex((item) => item.idEtapa === over.id);

      if (newIndex === -1) return;

      const newItems = [...etapas];
      const [movedItem] = newItems.splice(oldIndex, 1);
      newItems.splice(newIndex, 0, movedItem);

      // Atualizar ordens
      const updatedItems = newItems.map((item, index) => ({
        ...item,
        ordem: index + 1,
      }));

      onEtapasChange(updatedItems);
    }
  }

  const handleUpdateWeight = (id: string, newWeight: number) => {
    const updated = etapas.map((etapa) =>
      etapa.idEtapa.toString() === id ? { ...etapa, peso: newWeight } : etapa
    );
    onEtapasChange(updated);
  };

  const handleDelete = (id: string) => {
    onEtapasChange(etapas.filter((etapa) => etapa.idEtapa.toString() !== id));
  };

  const handleAddEtapa = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const etapa = etapasSelect.find(e => e.idEtapa === etapaSelecionada);
    if (!etapa) return;

    const etapaNova = {
      idEtapa: etapa.idEtapa,
      nome: etapa.nome,
      ordem: etapas.length + 1,
      peso: 1
    } as EtapaEdicao;

    const novaLista = [...etapas, etapaNova];
    onEtapasChange(novaLista);
    setOpen(false);
    setEtapaSelecionada(0);
  };

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography fontSize={20} fontWeight={600}>
            Etapas da Edição
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            Adicionar Etapa
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="tabela de etapas da edição">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "50px" }}>&nbsp;</TableCell>
                <TableCell>Ordem</TableCell>
                <TableCell>Etapa</TableCell>
                <TableCell>Peso</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={etapas.map((etapa) => etapa.idEtapa)} strategy={verticalListSortingStrategy}>
                  {etapas.map((etapa) => (
                    <SortableTableRow
                      key={etapa.idEtapa}
                      row={etapa}
                      onUpdateWeight={handleUpdateWeight}
                      onDelete={handleDelete}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <FormModal
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleAddEtapa}
        title="Adicionar Etapa na Edição"
        description="Escolha uma etapa disponível para adicionar."
        submitLabel="Adicionar"
        isSubmitDisabled={etapaSelecionada === 0}
        emptyMessage={
          etapasSelect.length === 0
            ? "Nenhuma etapa disponível para adicionar."
            : undefined
        }
      >
        <FormControl fullWidth margin="normal">
          <InputLabel id="etapa-label">Etapa *</InputLabel>
          <Select
            labelId="etapa-label"
            id="s2-tipo-graduacao"
            label="Etapa *"
            value={etapaSelecionada}
            onChange={(e) => setEtapaSelecionada(Number(e.target.value))}
          >
            {etapasSelect.map((etapa) => (
              <MenuItem key={etapa.idEtapa} value={etapa.idEtapa}>
                {etapa.nome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </FormModal>
    </>
  );
};
